<template>
  <div>
    <v-breadcrumbs :items="breadcrumbItems"/>

    <script type="application/javascript" src="https://js.stripe.com/v3/"></script>
    <v-card>
      <div>
        <div>
          <v-card-title v-if="request.mode === 'quote'" class="subtitle">Complete Order</v-card-title>
          <v-card-title v-else class="subtitle">Confirm Order</v-card-title>
          <v-stepper v-model="step" v-if="request.mode !== 'quote'" alt-labels flat>
            <v-stepper-header>
              <v-stepper-step step="1">Confirm</v-stepper-step>
              <v-divider />
              <v-stepper-step step="2">Pay</v-stepper-step>
              <v-divider />
              <v-stepper-step step="3">Get Started</v-stepper-step>
            </v-stepper-header>
          </v-stepper>
          <v-card-text v-if="request.mode === 'quote'">
            <p>Order details have been finalized on a separate quote. Review the  quote for order details.</p>
            <p>This form will prepare your organization account and needs to be completed before the onboarding call.</p>
          </v-card-text>
          <div class="mx-1">
          <v-container v-if="selectedPlan || selectedSupportPlan" class="info-container">

            <v-row dense>
              <v-col cols="12" sm="9">
                <h3>Review order details</h3>
              </v-col>
              <v-col cols="12" sm="3">
                <v-btn-toggle v-model="request.currency" mandatory class="multiline-btns" >
                  <v-btn v-for="cur in currencies" :key="cur" :value="cur" small>
                    {{cur}}
                  </v-btn>
                </v-btn-toggle>
              </v-col>
            </v-row>
            <v-row v-if="request.mode === 'event'" class="field" dense>
              <v-col cols="12" sm="3" class="label">Product</v-col>
              <v-col cols="12" sm="6" class="value">
                On-demand Event (one-time)
                <p v-if="extendedDurationLabel" class="hint">{{ extendedDurationLabel }}</p>
                <p v-else class="hint">Duration up to 45-days. Extended duration available at a premium.</p>
              </v-col>
            </v-row>

            <v-row v-if="request.mode === 'subscription'" class="field" dense>
              <v-col cols="12" sm="3" class="label">Product</v-col>
              <v-col cols="12" sm="6" class="value">
                Unlimited Subscription
                <p class="hint">Monthly billing, annual agreement</p>
              </v-col>
            </v-row>

            <v-row v-if="selectedPlan" class="field" dense>
              <v-col cols="12" sm="3" class="label">Plan</v-col>
              <v-col cols="12" sm="6" class="value">
                {{ selectedPlan.license | license }}
                <p v-if="request.mode === 'event'" class="hint">{{request.currency}} {{perUserFee | currency }} per participant</p>
                <p v-if="request.mode === 'subscription'" class="hint">{{request.currency}} {{perUserFee | currency}} per participant per month</p>
              </v-col>
            </v-row>

            <v-row v-if="selectedPlan" class="field" dense>
              <v-col cols="12" sm="3" class="label">Participants</v-col>
              <v-col cols="12" sm="2" class="value">
                <v-text-field
                  v-model.number="request.quantity"
                  solo
                  type="number"
                  hide-details
                  @change="checkMinQuantity"
                  />
              </v-col>
              <v-col cols="8" sm="4" class="value">
                participants included
                <p class="hint">More can be added later</p>
              </v-col>
              <v-col cols="4" sm="3" class="price">
                <span class="small me-1">{{request.currency}}</span>
                <span class="large me-1">{{totalUserFee | int}}</span>
                <span v-if="request.mode === 'subscription'" class="small me-1">(per month)</span>
                <p class="text-muted">{{request.currency}} {{ perUserFee | currency }} * {{ request.quantity }}</p>
              </v-col>
            </v-row>
            <v-row v-if="quantityMessage" class="field pb-4" dense>
              <v-col cols="12" sm="3" class="label">
              </v-col>
              <v-col cols="12" sm="9">
                <inline-tip >{{quantityMessage}}</inline-tip>
              </v-col>
            </v-row>

            <v-row v-if="selectedSupportPlan" class="field" dense>
              <v-col cols="12" sm="3" class="label">
                Support Plan
              </v-col>
              <v-col cols="8" sm="6" class="value">
                {{ selectedSupportPlan.name }}
                <p v-if="oneTimeFee == 0" class="hint">You can start preparing your event immediately after completing the payment.</p>
                <p v-else class="hint">You can book your onboarding call after completing the payment.</p>
              </v-col>
              <v-col cols="4" sm="3" class="price">
                <span class="small me-1">{{request.currency}}</span>
                <span class="large me-1">{{oneTimeFee | int}}</span>
                <span v-if="request.mode === 'subscription'" class="large me-1">(one-time)</span>
              </v-col>
            </v-row>

            <v-divider v-if="request.mode === 'event'" />

            <v-row v-if="request.mode === 'event'" class="field" dense>
              <v-col cols="8" sm="9" class="label">
                Total
              </v-col>
              <v-col cols="4" sm="3" class="price">
                <span class="small me-1">{{request.currency}}</span><span class="large">{{totalFee | int}}</span>
              </v-col>
            </v-row>
          </v-container>
          </div>

          <v-card-text>
            <v-form ref="form">
              <div v-if="!user">
                <h3>Your details</h3>
                <div v-if="!user" class="admin-input-container mt-4 mb-8">
                  <p>
                    Already have an account? 
                    <v-btn outlined color="primary" class="ms-2" @click="$refs.loginForm.open(request.email, 'LOGIN')">Login Now</v-btn>
                    <v-btn text small class="ms-2" color="primary" to="/account/passwordrecovery">{{$t('account.login.forgot-your-password')}}</v-btn>
                  </p>
                </div>
              </div>

              <v-text-field
                v-if="!user"
                v-model="request.name"
                :rules="nameRules"
                label="Your name"
                required
                />

              <v-text-field
                v-if="!user"
                v-model="request.email"
                :rules="emailRules"
                label="Your email"
                required
                hint="We will send instructions to help you get started when your payment is completed."
                />

              <v-text-field
                v-if="!user"
                v-model="password"
                :rules="passwordRules"
                :label="$t('account.register.password')"
                required
                :append-icon="passwordShow ? 'fal fa-eye-slash' : 'fal fa-eye'"
                :type="passwordShow ? 'text' : 'password'"
                @click:append="passwordShow = !passwordShow"
                />

              <div v-if="orgs && orgs.length">
                <h3>Organization</h3>
                <p>This order will be added to the selected organization. From there you can assign it to any event within this organization.</p>
                <v-radio-group v-model="orgMode">
                  <v-radio
                    value="CREATE"
                    label="Create a new organization"
                    hide-details
                    @change="request.org=''"
                    />
                  <v-text-field v-if="orgMode == 'CREATE'"
                    v-model="request.org"
                    label="Name of the organization"
                    class="ml-8"
                      :rules="orgRules"
                    />
                  <v-radio
                    value="LINK"
                    label="Link to an existing organization"
                    hide-details
                    />
                  <v-select v-if="orgMode == 'LINK' && orgs"
                    v-model="request.org"
                    :items="orgs"
                    item-text="name"
                    item-value="name"
                    label="Select your organization"
                    class="ml-8"
                    :rules="orgRules"
                    />
                </v-radio-group>              
              </div>
              <div v-else>

              <v-text-field
                v-model="request.org"
                label="Organization Name"
                required
                :rules="orgRules"
                />
              </div>

              <v-checkbox 
                v-if="request.mode === 'subscription'"
                v-model="request.contract_iterations" 
                :value="12" 
                label="I understand this is an annual agreement with 12 monthly payments." 
                hide-details 
                :rules="subscriptionRules" 
                />

              <v-checkbox v-model="request.terms" ref="termsCheckbox" :rules="termsRules">
                <template v-slot:label>
                  <div class="d-flex" style="flex-direction:column; text-align:left;">
                    <div>I have read the <a href='/privacypolicy' target="_blank">Privacy Policy</a>  and have read and agree to the <a href='/cloudterms' target="_blank">Cloud Terms and Conditions</a>.</div>
                    <div class="text-muted">Also, I agree that my personal data is used as described in our privacy policy and might be transferred and processed outside my country of origin.</div>
                  </div>
                </template>
              </v-checkbox>

            </v-form>
          </v-card-text>

          <v-card-text>      
            <v-alert v-if="error" type="warning">{{error}}</v-alert>
            <v-alert v-if="!valid" type="info">{{validationMessage}}</v-alert>

            <v-btn x-large :disabled="error" color="primary" @click="pay" :loading="$store.getters.isLoading">
              {{ request.mode === 'quote' ? 'Confirm' : 'Confirm &amp; Pay' }}
            </v-btn>
            <p v-if="request.mode !== 'quote'" class="text-muted mt-4">We use Stripe to safely and securely accept payments. Prefer to pay by ACH, or need an invoice with PO number? <router-link to="/contact">Contact Us</router-link>.</p>
          </v-card-text>   
        </div>    
      </div>    
      
    </v-card>
    <LoginOrRegisterDialog ref="loginForm" mode="REGISTER" message="In order to complete your payment you'd need to either create a new account or login with your current account." @submit="loggedIn" />

  </div>      
</template>



<script>
import { mapGetters } from "vuex";
import { EventBus } from '@/plugins/eventbus.js';
import checkoutService from "@/services/checkoutService";
import profileService from "@/services/profileService";
import orgManagerService from "@/services/orgManagerService";
import siteData from '@/data/site.json'
import LoginOrRegisterDialog from '@/components/LoginOrRegisterDialog.vue';
import InlineTip from '@/components/InlineTip.vue';

export default {
  name: "Profile",
  components: {
    LoginOrRegisterDialog,
    InlineTip
  },
  props: {
  },
  data() {
    return {
      breadcrumbItems: [
        { text: 'Checkout', disabled: true },
        { text: 'Confirm', disabled: true },
      ],
      step:1,
      siteData: siteData,
      valid: false,
      error: null,
      validationMessage: null,
      quantityMessage: null,

      products: siteData.products,
      currencies: siteData.currencies,

      orgs: null,
      orgMode: 'CREATE',

      password: null,
      passwordShow: false,

      nameRules: [
        v => !!v || 'Please enter your name',
      ],
      emailRules: [
        v => !!v || 'E-mail is required',
        v => /.+@.+\..+/.test(v) || 'E-mail must be valid',
      ],
      passwordRules: [
        v => !!v || 'Password is Required',
        v => !!v && v.length >= 6 || 'Password must be at least 6 characters or more'
      ],
      orgRules: [
        v => !!v || 'Please enter the name of your organization',
      ],
      termsRules: [
        v => !!v || 'Please accept our terms of service',
      ],
      subscriptionRules: [
        v => !!v || 'Please confirm you agree with the subscription terms',
      ],
      request: {
        name: null,
        email: null,
        org: null,
        terms: false,
        contract_iterations: null,
        currency: null,
        mode: 'event',
        plan: null,
        quantity: 0,
        support: null,
      },
    };
  },
  async mounted() {
    await this.loadState();
    
    EventBus.$on('login-state-change', async user => {
      await this.loadUserState();
    });
    await this.loadUserState();
  },
  methods: {
    async loadUserState() {
      if (this.user) {
        if (!this.orgs) {
          this.orgs = (await orgManagerService.getOwnedEvents()).data.data;
          if (this.orgs && this.orgs.length) {
            this.orgMode = 'LINK',
            this.request.org = this.orgs[0].name;
          }
          var profile = (await profileService.get(false)).data;
          if (profile) {
            this.request.name = profile.name;
            this.request.email = profile.email;
            if (profile.default_org_id && this.orgs.find(x => x.id == profile.default_org_id)) {
              this.request.org = this.orgs.find(x => x.id == profile.default_org_id).name;
            }
          }
        }
      }
    },
    async loadState() {
      this.request.currency =  (this.$route.query.currency || 'USD').toUpperCase();
      this.request.quantity =  this.$route.query.participants;
      this.request.plan =  this.$route.query.license;
      this.request.support =  this.$route.query.support;
      this.request.mode =  this.$route.query.mode;
      this.request.extended_duration =  this.$route.query.extendedDuration;

      // todo: load user profile and pre-fill when logged in
      //this.request.name = 'Koen';
      //this.request.email = 'shops@neokoen.com';
      //this.request.org = 'Koens Inc';

      if (this.request.mode == 'quote') {
        this.valid = true;
      }
      else if (this.request.mode == 'product') {
        this.valid = true;
      }
      else if (this.request.mode == 'support') {
        this.valid = this.request.currency && this.request.support;
      }
      else {
        this.valid = this.request.currency 
                  && this.request.quantity && this.request.quantity > 0
                  && this.request.plan 
                  && this.request.mode;
      }
      if (!this.valid) {
        this.validationMessage = 'Please reach out to support to request a new order confirmation link.';
      }
    },
    async loggedIn(user) {
      if (user) {
        this.pay();
      }
    },
    async pay() {
      var form = this.$refs.form;
      if (!form.validate()) {
        this.valid = false;
        this.validationMessage = 'Please fill in all fields.';
      }
      else if (this.request.mode !== 'quote' && (!this.selectedPlan && !this.selectedSupportPlan)) {
        this.valid = false;
        this.validationMessage = 'Unknown product selected. Please contact support.';
      }
      else {
        if (this.user) {
          if (this.request.mode === 'quote') {
            try {
              var orgResponse = await orgManagerService.create({
                name: this.request.org,
              });
              this.$router.push({ name: 'orgmanagerView', params: { id: orgResponse.data.id }, query: { mode: 'new-quote' } });
            }
            catch (ex) {
              this.error = ex.response.data.msg;
              console.log('ERR', ex, ex.response);
            }
            
            return;
          }
          const response = await checkoutService.confirm({ 
            plan_product_key: this.selectedPlan && this.selectedPlan.key,
            support_plan_product_key: this.selectedSupportPlan && this.selectedSupportPlan.key,
            ...this.request 
          });
          if (response.data.status == 'OK') {
            this.valid = true;

            var stripe = Stripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
            const stripeResponse = stripe.redirectToCheckout({ sessionId: response.data.id });
            if (stripeResponse.error) {
              this.error = stripeResponse.error;
            }
          }
          else {
            this.valid = false;
            this.validationMessage = response.data.msg;
          }
        }
        else {
          try {
            await this.$refs.loginForm.registerOrLogin(this.request.email, this.password, this.request.name, /*throw*/ true);
          }
          catch (ex) {
            this.valid = false;
            this.validationMessage = `Error creating account: ${ex.message}`;
          }
        }
      }
    },
    checkMinQuantity() {
      console.log('/// check', this.request.quantity);
      if ((this.request.support == 'setup-service' || this.request.support == 'full-service') && this.request.quantity < 50) {
        this.request.quantity = 50;
        this.quantityMessage = 'Minimum purchase quantity is 50';
      }
      else if ((this.request.support == 'self-service') && this.request.quantity < 10) {
        this.request.quantity = 10;
        this.quantityMessage = 'Minimum purchase quantity is 10';
      }
      else {
        this.quantityMessage = null;
      }
    }
  },
  computed: {
    extendedDurationLabel() {
      if (this.request.extended_duration == '1q') {
        return 'Duration up to 100 days / 3 months / 1 quarter.';
      }
      return null;
    },
    extendedDurationPostfix() {
      if (this.request.extended_duration && this.request.extended_duration == '1q') {
        return "-" + this.request.extended_duration.toLowerCase();
      }
      return "";
    },
    selectedPlan() {
      if (this.request.mode == 'event') {
        return this.request.plan && this.products.find(x=>x.key==`product-event-${this.request.plan.toLowerCase()}${this.extendedDurationPostfix}`);
      }
      return this.request.plan && this.products.find(x=>x.key==`subscription-month-${this.request.plan.toLowerCase()}`);
    },
    selectedSupportPlan() {
      if (this.request.support == 'self-service') {
        return this.products.find(x=>x.key=='product-self-service');
      }
      if (this.request.support == 'setup-service') {
        return this.products.find(x=>x.key=='product-setup-service');
      }
      return this.request.support && this.products.find(x=>x.key=='product-full-service');
    },
    perUserFee() {
      if (!this.selectedPlan) {
        return null;
      }
      if (this.showSubscriptionDiscount) {
        return this.selectedPlan.discounted_prices ? this.selectedPlan.discounted_prices[this.request.currency] : this.selectedPlan.prices[this.request.currency];
      }
      return this.selectedPlan.prices[this.request.currency];
    },
    oneTimeFee() {
      if (!this.selectedSupportPlan) {
        return 0;
      }
      return this.selectedSupportPlan.prices[this.request.currency];
    },
    totalUserFee() {
      return this.perUserFee * this.request.quantity;
    },
    totalFee() {
      return this.oneTimeFee + this.totalUserFee;
    },

    ...mapGetters({
      user: "user"
    })
  },

};
</script>


<style lang="scss">
    .info-container {
      .field {
        //margin-bottom: 16px;
        .label { }
        .value { font-weight: bold; }
        .hint { font-size: 80%; font-weight: normal; }
      }
    }

</style>

